import axios from 'axios';

// API base URL
const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// Configure axios with the base URL
const api = axios.create({
  baseURL: API_URL,
});

// Add request interceptor to add authentication token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API service object
const apiService = {
  // Backtest endpoints
  backtest: {
    // Run a backtest
    run: (data) => api.post('/backtest/run', data),
    
    // Get available strategies
    getStrategies: () => api.get('/backtest/strategies'),
    
    // Get backtest results list
    getResults: (limit = 10, offset = 0) => 
      api.get(`/backtest/results?limit=${limit}&offset=${offset}`),
    
    // Get a specific backtest result
    getResult: async (resultId) => {
      // Try primary (singular) endpoint first
      try {
        const response = await api.get(`/backtest/result/${resultId}`);
        console.log('=== API RESPONSE STRUCTURE ===');
        console.log('Response keys:', Object.keys(response.data));
        console.log('Premium data type:', typeof response.data.premium_data);
        console.log('Premium data array?', Array.isArray(response.data.premium_data));
        console.log('Premium data length:', response.data.premium_data ? response.data.premium_data.length : 0);
        if (response.data.premium_data && response.data.premium_data.length > 0) {
          console.log('First premium data item:', response.data.premium_data[0]);
        }
        return response;
      } catch (primaryErr) {
        const status = primaryErr?.response?.status;
        const detail = primaryErr?.response?.data?.detail || primaryErr?.message;
        console.warn(`Primary /backtest/result/${resultId} failed:`, status, detail);
        // Fallback to old plural endpoint for compatibility
        try {
          const fallback = await api.get(`/backtest/results/${resultId}`);
          console.log('Fallback endpoint succeeded.');
          return fallback;
        } catch (fallbackErr) {
          const fStatus = fallbackErr?.response?.status;
          const fDetail = fallbackErr?.response?.data?.detail || fallbackErr?.message;
          console.error(`Fallback /backtest/results/${resultId} failed:`, fStatus, fDetail);
          throw primaryErr; // propagate original error
        }
      }
    },
    
    // Delete a backtest result
    deleteResult: (resultId) => api.delete(`/backtest/results/${resultId}`),
  },
  
  // Settings endpoints
  settings: {
    // Get user settings
    get: () => api.get('/settings'),
    
    // Update user settings
    update: (data) => api.put('/settings', data),
  },
  
  // Common endpoints for reference data
  common: {
    // Get available symbols (hard-coded from config)
    getSymbols: () => {
      return Promise.resolve({
        data: [
          { id: 'nifty', name: 'NIFTY' },
          { id: 'banknifty', name: 'BANKNIFTY' },
          { id: 'finnifty', name: 'FINNIFTY' },
        ]
      });
    },
  },
  
  // Market Data endpoints
  marketData: {
    // Get index OHLC data for a specific symbol and date range
    getIndexData: (symbol, startDate, endDate, timeframe = undefined) => {
      const payload = {
        symbol,
        start_date: startDate,
        end_date: endDate,
      };
      if (timeframe !== undefined && timeframe !== null && !Number.isNaN(Number(timeframe))) {
        payload.timeframe = Number(timeframe);
      }
      return api.post('/market-data/index', payload);
    },
  },

  // Option Chain endpoints
  optionChain: {
    // Get expiries for a symbol
    getExpiries: (symbol) => api.get('/option-chain/expiries', { params: { symbol } }),
    // Get option chain snapshot
    getSnapshot: (payload) => api.post('/option-chain/snapshot', payload),
  },

  // Data endpoints
  data: {
    getOverview: () => api.get('/data/overview'),
    getExpiries: (symbol, limit = 10) => api.get(`/data/expiries/${symbol}?limit=${limit}`),
    addExpiry: (symbol, expiry, wm = null) => api.post(`/data/expiries/${symbol}`, { expiry, wm }),
    updateExpiry: (symbol, old_expiry, new_expiry, wm = null) => api.put(`/data/expiries/${symbol}`, { old_expiry, new_expiry, wm }),
    deleteExpiry: (symbol, expiry) => api.delete(`/data/expiries/${symbol}/${expiry}`),
  },
};

export default apiService;
